"use client";

import { RefObject, useEffect } from "react";

interface UseClickOutsideOptions {
  /** Whether the click outside handler is active */
  enabled?: boolean;
  /** Delay before attaching the listener (useful to prevent immediate triggering) */
  delay?: number;
  /** Function to determine if a click should be ignored (e.g., clicking on a portal) */
  shouldIgnore?: (target: EventTarget | null) => boolean;
}

/**
 * Hook that triggers a callback when clicking outside of a referenced element.
 *
 * @param ref - Reference to the element to detect clicks outside of
 * @param callback - Function to call when a click outside is detected
 * @param options - Configuration options
 *
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * useClickOutside(ref, () => setIsOpen(false), { enabled: isOpen });
 * ```
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  callback: () => void,
  options: UseClickOutsideOptions = {},
): void {
  const { enabled = true, delay = 100, shouldIgnore } = options;

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Check if we should ignore this click (e.g., clicking on a portal)
      if (shouldIgnore?.(e.target)) {
        return;
      }

      if (!ref.current) return;

      // Check if the click target is inside the ref
      const targetIsInside = ref.current.contains(e.target as Node);

      // If target is not inside, also check click coordinates
      // This handles cases where pointer-events: none causes clicks to register on parent elements
      if (!targetIsInside) {
        const rect = ref.current.getBoundingClientRect();
        const clickX = e.clientX;
        const clickY = e.clientY;

        const clickIsInsideBounds =
          clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom;

        // If click coordinates are inside the modal bounds, don't close
        if (clickIsInsideBounds) {
          return;
        }

        // Click is truly outside - close the modal
        callback();
      }
    };

    // Delay attaching to prevent immediate triggering from the click that opened the element
    const timeout = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, delay);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback, enabled, delay, shouldIgnore]);
}
